import { List, Button, Typography, ListSubheader } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import useProjectFieldInfos from '@/hooks/useProjectFieldInfos'
import { useUpdateWorkflowState } from '@/service-library/hooks/workflow-states'
import queryKeys from '@/service-library/query-keys'
import { WorkflowState } from '@/types/workflows'
import { showErrorSnackbar } from '@/utils/snackbars'
import FieldListItem from '@/components/project-settings/FieldListItem'
import SettingsRow from '@/components/shared/settings/SettingsRow'
import SettingsSection from '@/components/shared/settings/SettingsSection'
import SetStateFieldsDialog from './SetStateFieldsDialog'

type WorkflowStateRelevantFieldsProps = {
  workflowState: WorkflowState
  detailQueryKey: QueryKey
  excludedFieldsIds: string[]
}

export default function WorkflowStateRelevantFields({
  workflowState,
  detailQueryKey,
  excludedFieldsIds,
}: WorkflowStateRelevantFieldsProps) {
  const setFieldsOverlay = useOverlay()

  const { updateWorkflowState } = useUpdateWorkflowState({
    sideEffectQueryKeys: [
      queryKeys.workflows.lists(), // Update it in case they've already fetched it on other pages
    ],
    detailQueryKey,
    onError: () => {
      showErrorSnackbar(
        'Unable to update fields in workflow state. Please try again later.',
      )
    },
  })

  const { fieldInfos, allFields } = useProjectFieldInfos()
  const allFieldIds = allFields.map((field) => field.id)

  return (
    <>
      <SettingsSection
        title="Relevant fields"
        description="The fields shown on the validation screen when a document is in this state."
        actions={
          <Button variant="text" onClick={setFieldsOverlay.open}>
            Set fields
          </Button>
        }
      >
        {excludedFieldsIds.length == allFields.length ? (
          <SettingsRow>
            <Typography variant="body2" color="text.secondary">
              <i>No fields selected.</i>
            </Typography>
          </SettingsRow>
        ) : (
          <List dense>
            {fieldInfos.map((fieldInfo) => {
              if (excludedFieldsIds.includes(fieldInfo.field.id)) return null
              return !fieldInfo.fields ? (
                <FieldListItem
                  key={fieldInfo.field.id}
                  field={fieldInfo.field}
                />
              ) : (
                <List key={fieldInfo.field.id} dense sx={{ my: 1 }}>
                  <ListSubheader
                    sx={{
                      background: (theme) => theme.palette.background.paper,
                      zIndex: 2,
                    }}
                  >
                    {fieldInfo.field.name}
                  </ListSubheader>
                  {fieldInfo.fields.map((subField) => {
                    if (excludedFieldsIds.includes(subField.id)) return null
                    return (
                      <FieldListItem
                        key={subField.id}
                        field={subField}
                        indented
                      />
                    )
                  })}
                </List>
              )
            })}
          </List>
        )}
      </SettingsSection>

      <SetStateFieldsDialog
        overlay={setFieldsOverlay}
        fieldInfos={fieldInfos}
        allFieldIds={allFieldIds}
        excludedFieldsIds={excludedFieldsIds}
        onSave={(excludedGridFieldsIds) => {
          updateWorkflowState({
            ...workflowState,
            excluded_project_grid_fields_ids: excludedGridFieldsIds,
          })
        }}
      />
    </>
  )
}
